<template lang="pug">
.page-view(id="page", :class="`${maxWidth ? 'page-view--max-width' : ''} ${largeMaxWidth ? 'page-view--large-max-width' : ''}`")
  .page-header(:class="`${hasTabs ? 'page-header--no-border' : ''}`")
    .title(:class="`${icon ? 'has-icon' : ''}`") {{ title | truncate }}
    .id(v-if="id", @click="copy(id)") # {{ id }}
  .page-tabs(v-if="hasTabs", :class="`${tabsOverflown ? 'page-tabs--overflown' : ''}`")
    slot(name="tabs")
    .expand-tabs: .dots(@click="showHiddenTabs = !showHiddenTabs", :class="`${showHiddenTabs ? 'show' : ''}`"): i.mdi.mdi-dots-horizontal
    .expand-tabs-list(:class="`${showHiddenTabs ? 'show' : ''}`")
  .page-content(:class="`${maxWidth ? 'max-width' : ''} ${fullWidth ? 'page-content--full-width' : ''} ${hasTabs ? 'page-content--with-tabs' : ''}`")
    .page-main(:class="`${contentLoading ? 'page-main--loading' : ''}`")
      template(v-if="contentLoading")
        .loading: i.mdi.mdi-loading.mdi-spin
      template(v-else)
        slot
    .page-side(v-if="hasSide")
      template(v-if="!contentLoading")
        slot(name="side")
  .page-bottom(:class="`${maxWidth ? 'max-width' : ''} ${fullWidth ? 'page-bottom--full-width' : ''}`", v-if="hasFooter")
    .page-bottom-main
      slot(name="bottom")
    .page-bottom-side
      slot(name="bottom-side")
</template>

<script>

import { truncate, isOverflown } from '@/utils';

export default {
  props: {
    title: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: '',
    },
    maxWidth: {
      type: Boolean,
      default: false,
    },
    largeMaxWidth: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    contentLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasTabs() {
      return !!this.$slots.tabs;
    },
    hasSide() {
      return !!this.$slots.side;
    },
    hasFooter() {
      return !!this.$slots.bottom;
    },
  },
  methods: {
    /**
     * Function for parent to run
     * Will run a series of functions internally
     */
    changedTab() {
      this.clearExpand();
      this.resizeEvent();
    },
    copy() {},
    fixTabs() {
      const tabsEl = this.$el.querySelector('.page-tabs');
      if (!tabsEl) {
        return;
      }
      const tabsElStyle = tabsEl.currentStyle || window.getComputedStyle(tabsEl);
      const tabsElWidth = parseFloat(tabsElStyle.width) - parseFloat(tabsElStyle.paddingLeft) - parseFloat(tabsElStyle.paddingRight);
      const tabExpand = tabsEl.querySelector('.expand-tabs-list');
      const tabsExpand = tabExpand.querySelectorAll('.tab');

      // Return all tabs to mothership
      tabsExpand.forEach(tab => tabsEl.appendChild(tab));

      [...tabsEl.querySelectorAll('.tab')].sort((a, b) => parseInt(a.getAttribute('data-tabindex'), 10) - parseInt(b.getAttribute('data-tabindex'), 10)).forEach(tab => tabsEl.appendChild(tab));

      const tabs = tabsEl.querySelectorAll('.tab');

      let usedWidth = this.tabsOverflown ? 30 : 10;
      let maxWidth = false;

      const [activeTab] = [...tabs].sort((a, b) => b.classList.contains('tab--active') - a.classList.contains('tab--active'));
      const activeTabIndex = [...tabsEl.children].indexOf(activeTab);

      const activeTabStyle = activeTab.currentStyle || window.getComputedStyle(activeTab);
      const activeTabWidth = parseFloat(activeTabStyle.width) + parseFloat(activeTabStyle.marginRight);

      usedWidth += activeTabWidth;

      tabs.forEach((tab) => {
        if (tab === activeTab) {
          return;
        }
        const tabStyle = tab.currentStyle || window.getComputedStyle(tab);
        const tabWidth = parseFloat(tabStyle.width) + parseFloat(tabStyle.marginRight);

        if (maxWidth) {
          tabExpand.appendChild(tab);
          return;
        }

        if ((usedWidth + tabWidth) < tabsElWidth) {
          const tabIndex = [...tabsEl.children].indexOf(tab);
          if (tabIndex < activeTabIndex) {
            tabsEl.insertBefore(tab, activeTab);
          } else {
            tabsEl.appendChild(tab);
          }
          usedWidth += tabWidth;
          maxWidth = false;
        } else {
          maxWidth = true;
          tabExpand.appendChild(tab);
        }
      });

      // eslint-disable-next-line
      this.tabsOverflown = tabExpand.childElementCount === 0 ? false : true;
    },
    resizeEvent() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        const tabsEl = this.$el.querySelector('.page-tabs');
        if (!tabsEl) {
          return;
        }
        const tabExpand = tabsEl.querySelector('.expand-tabs-list');
        const overflown = isOverflown(tabsEl);

        if (overflown || tabExpand.childElementCount > 0) {
          this.tabsOverflown = true;
        }

        if (!overflown && tabExpand.childElementCount === 0) {
          this.tabsOverflown = false;
        }

        this.fixTabs();
      }, 1);
    },
    clearExpand() {
      this.showHiddenTabs = false;
    },
  },
  data() {
    return {
      tabsOverflown: false,
      resizeTimeout: null,
      showHiddenTabs: false,
    };
  },
  mounted() {
    this.resizeEvent();
    window.addEventListener('resize', this.resizeEvent);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeEvent);
  },
  filters: {
    truncate(value) {
      return truncate.apply(value, [100, true]);
    },
  },
};
</script>

<style lang="scss">
.page-view {
  .page-content {
    p {
      font-size: 1rem;

      a {
        color: #0060ff;
      }

      &.small {
        color: lighten(black, 50%);
        font-weight: 500;
        font-size: .9rem;
      }

      &.italic {
        font-style: italic;
      }
    }
  }
}
</style>

<style lang="scss" scoped>

$menuWidth: 250px;
$maxMobileWidth: 767px;
$mainWidth: 768px;
$sideWidth: 320px;

.page-view {
  pointer-events: none;

  & > *:not(.page-header) {
    pointer-events: all;
  }

  @media (max-width: $maxMobileWidth) {
    &.fixed {
      position: fixed;
      z-index: 4;
      left: 0;
      right: 0;
    }
  }

  @media (min-width: $mainWidth) {
    position: absolute;
    // overflow: auto;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    -webkit-overflow-scrolling: touch;
  }

  &.page-view--max-width {
    & > * {
      max-width: 1280px;
    }
  }
  &.page-view--large-max-width {
    & > * {
      max-width: 1680px;

      .page-main {
        max-width: 1024px !important;
      }
    }
  }
}

.page-header {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  padding: 25px 40px;

  &.page-header--no-border {
    border-bottom: 0;
  }

  @media (min-width: 768px) {
    padding: 40px;
  }

  .title {
    color: #073347;
    font-size: 24px;
    font-weight: 500;
    line-height: 30px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (min-width: 768px) {
      font-size: 42px;
      line-height: 48px;
      margin-left: 0;
    }
  }
}

.page-tabs {
  display: flex;
  padding: 0 20px;
  position: relative;

  @media (min-width: $mainWidth) {
    padding: 0 40px;
  }

  @media (min-width: calc(#{$mainWidth} + #{$menuWidth})) {
    padding: 0 40px;
  }

  .expand-tabs {
    align-items: center;
    display: none;
    justify-content: center;
    margin-left: auto;
    order: 1;
    user-select: none;

    .dots {
      align-items: center;
      justify-content: center;
      background-color: white;
      border-radius: 20px;
      display: flex;
      color: #0060ff;
      width: 36px;
      height: 36px;
      user-select: none;
      outline: none;
      -webkit-tap-highlight-color:  rgba(255, 255, 255, 0);

      i {
        font-size: 1.5rem;
        line-height: 1.5rem;
      }

      &:hover {
        cursor: pointer;
      }

      &.show {
        color: red;
      }

      @media (min-width: 1024px) {
        height: 40px;
        width: 40px;
      }
    }
  }

  .expand-tabs-list {
    display: none;
    flex-direction: column;
    position: absolute;
    right: 10px;
    top: 100%;
    width: 200px;
    user-select: none;
    z-index: 10;

    & > * {
      border-radius: 0;
      width: 100%;
      &:first-child {
        border-radius: 16px 16px 0 0;
      }
      &:last-child {
        border-radius: 0 0 16px 16px;
      }
      &:only-child {
        border-radius: 16px;
      }

      &.tab--active {
        border-bottom: 2px solid #efefef;
      }
    }

    &.show {
      display: flex;
    }
  }

  &.page-tabs--overflown {
    .expand-tabs {
      display: flex;
    }
  }
}

.page-content {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  padding: 20px;

  .page-main--loading {
    & > *:not(.loading) {
      display: none;
    }
  }

  .loading {
    align-items: center;
    justify-content: center;
    display: flex;
    width: 100%;
    height: 300px;

    i {
      font-size: 3rem;
    }
  }

  @media (min-width: $mainWidth) {
    padding: 40px;
  }

  @media (min-width: calc(#{$mainWidth} + #{$menuWidth})) {
    flex-direction: row;
    padding: 40px;
  }

  .page-main {
    max-width: 728px;
    width: 100%;

    @media (min-width: calc(#{$mainWidth} + #{$menuWidth})) {
      flex: 2;
      max-width: 688px;
    }
  }

  .page-side {
    margin-top: 20px;
    width: 100%;

    @media (min-width: calc(#{$mainWidth} + #{$menuWidth})) {
      margin-top: 0;
      min-width: 250px;
      margin-left: 40px;
      flex: 1;
      max-width: $sideWidth;
    }
  }

  &.page-content--with-tabs {
    padding-top: 0;

    .page-main {
      background-color: #F5F6F7;
      border-radius: 0 16px 16px 16px;
      box-shadow: 0 6px 12px 0 rgba(0,0,0,0.04), 0 2px 4px 0 rgba(0,0,0,0.04);
      padding: 20px;
    }
  }

  &.page-content--full-width {
    .page-main {
      max-width: unset;
    }
  }

}

.page-bottom {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  padding: 0 20px 20px;

  @media (min-width: $mainWidth) {
    padding: 0 40px 40px;
  }

  @media (min-width: calc(#{$mainWidth} + #{$menuWidth})) {
    flex-direction: row;
    padding: 0 40px 40px;
  }

  .page-bottom-main {
    max-width: 728px;
    width: 100%;

    @media (min-width: calc(#{$mainWidth} + #{$menuWidth})) {
      flex: 2;
      max-width: 688px;
    }
  }

  .page-bottom-side {
    margin-top: 20px;
    width: 100%;

    @media (min-width: calc(#{$mainWidth} + #{$menuWidth})) {
      margin-top: 0;
      min-width: 250px;
      margin-left: 40px;
      flex: 1;
      max-width: $sideWidth;
    }
  }
}
</style>
